import { Navigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { useFiscalStore } from "@/store/fiscal-store";
import { fmtBRL, fmtNum } from "@/lib/format";
import { 
  Package, TrendingUp, TrendingDown, DollarSign, Search, 
  Calendar, PieChart as PieIcon, BarChart3, Info, AlertCircle, Layers,
  ArrowRight, ArrowLeft, ArrowDownUp
} from "lucide-react";
import { 
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, 
  CartesianGrid, PieChart, Pie, Cell, Legend, LineChart, Line
} from "recharts";
import { cn } from "@/lib/utils";

interface ProdutoAgregado {
  xProd: string;
  ncm: string;
  cfop: string;
  qtd: number;
  vTotal: number;
  vTribTotal: number;
  cargaTributaria: number;
  ocorrencias: number;
  cluster?: 'A' | 'B' | 'C';
}

const DIAS_SEMANA = ["Domingo", "Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado"];

const COLORS = ["#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#EC4899"];

export default function ProdutosPage() {
  const { filteredNotas, dashboard } = useFiscalStore();
  const [q, setQ] = useState("");
  const [sortKey, setSortKey] = useState<keyof ProdutoAgregado>("vTotal");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [page, setPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  useEffect(() => { document.title = "Product Analytics — DataGrid XML"; }, []);

  const insights = useMemo(() => {
    const pMap = new Map<string, ProdutoAgregado>();
    const dMap = new Array(7).fill(0).map((_, i) => ({ day: DIAS_SEMANA[i], valor: 0, qtd: 0, impostos: 0 }));
    const ncmMap = new Map<string, { ncm: string; vTotal: number; vTrib: number; count: number }>();
    let vTotalProd = 0;
    let vTotalTrib = 0;

    for (const nota of filteredNotas) {
      const data = new Date(nota.emissao);
      const diaSemana = isNaN(data.getTime()) ? -1 : data.getDay();
      
      for (const item of nota.itens ?? []) {
        const key = `${item.ncm}||${item.xProd}`;
        const vItem = item.vProd || 0;
        const vTrib = (item.vICMS || 0) + (item.vPIS || 0) + (item.vCOFINS || 0) + (item.vIPI || 0);
        vTotalProd += vItem;
        vTotalTrib += vTrib;

        if (diaSemana !== -1) {
          dMap[diaSemana].valor += vItem;
          dMap[diaSemana].qtd += item.qCom;
          dMap[diaSemana].impostos += vTrib;
        }

        // NCM Aggregation
        const nKey = item.ncm || "00000000";
        const nExisting = ncmMap.get(nKey);
        if (nExisting) {
          nExisting.vTotal += vItem;
          nExisting.vTrib += vTrib;
          nExisting.count += 1;
        } else {
          ncmMap.set(nKey, { ncm: nKey, vTotal: vItem, vTrib, count: 1 });
        }

        const existing = pMap.get(key);
        if (existing) {
          existing.qtd += item.qCom;
          existing.vTotal += vItem;
          existing.vTribTotal += vTrib;
          existing.ocorrencias += 1;
        } else {
          pMap.set(key, {
            xProd: item.xProd,
            ncm: item.ncm,
            cfop: item.cfop,
            qtd: item.qCom,
            vTotal: vItem,
            vTribTotal: vTrib,
            cargaTributaria: 0,
            ocorrencias: 1,
          });
        }
      }
    }

    const ncmSorted = [...ncmMap.values()].sort((a, b) => b.vTotal - a.vTotal).slice(0, 10);
    const sorted = [...pMap.values()].sort((a, b) => b.vTotal - a.vTotal);
    let cumulative = 0;
    const clusters = { A: 0, B: 0, C: 0 };

    sorted.forEach(p => {
      p.cargaTributaria = p.vTotal > 0 ? (p.vTribTotal / p.vTotal) * 100 : 0;
      cumulative += p.vTotal;
      const ratio = vTotalProd > 0 ? cumulative / vTotalProd : 0;
      if (ratio <= 0.8) { p.cluster = 'A'; clusters.A += p.vTotal; }
      else if (ratio <= 0.95) { p.cluster = 'B'; clusters.B += p.vTotal; }
      else { p.cluster = 'C'; clusters.C += p.vTotal; }
    });

    return {
      produtos: sorted,
      diasSemana: dMap,
      ncmData: ncmSorted.map(n => ({ ...n, carga: n.vTotal > 0 ? (n.vTrib / n.vTotal) * 100 : 0 })),
      abcData: [
        { name: "Classe A (80%)", value: clusters.A, color: "#FF7A00" },
        { name: "Classe B (15%)", value: clusters.B || 1, color: "#FF9A3D" },
        { name: "Classe C (5%)", value: clusters.C || 1, color: "#334155" },
      ],
      vTotalProd,
      vTotalTrib,
      cargaMedia: vTotalProd > 0 ? (vTotalTrib / vTotalProd) * 100 : 0
    };
  }, [filteredNotas]);

  const filtrados = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return insights.produtos
      .filter((p) => !ql || p.xProd.toLowerCase().includes(ql) || p.ncm.includes(ql) || p.cfop.includes(ql))
      .sort((a, b) => {
        const va = a[sortKey] ?? 0;
        const vb = b[sortKey] ?? 0;
        return sortDir === "desc" ? (vb as number) - (va as number) : (va as number) - (vb as number);
      });
  }, [insights.produtos, q, sortKey, sortDir]);

  const slice = filtrados.slice(page * itemsPerPage, (page + 1) * itemsPerPage);
  const totalPages = Math.max(1, Math.ceil(filtrados.length / itemsPerPage));

  const top30 = useMemo(() => 
    insights.produtos.slice(0, 30).map(p => ({
      name: p.xProd.length > 25 ? p.xProd.slice(0, 25) + "..." : p.xProd,
      valor: p.vTotal,
      carga: p.cargaTributaria
    }))
  , [insights.produtos]);

  const toggleSort = (key: keyof ProdutoAgregado) => {
    if (sortKey === key) setSortDir((d) => (d === "desc" ? "asc" : "desc"));
    else { setSortKey(key); setSortDir("desc"); }
  };

  return (
    <div className="p-4 lg:p-6 max-w-[1600px] mx-auto space-y-8 animate-in fade-in duration-700">
      {/* Header Bento Section */}
      <header className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 border-l-4 border-primary pl-8 py-4 mb-6 bg-white/[0.02] glass-technical border-y-0 border-r-0 rounded-r-3xl">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Layers className="size-3 text-primary animate-pulse" />
            <span className="text-[10px] font-black uppercase tracking-[0.4em] text-primary">Item Level Intelligence Hub</span>
          </div>
          <h1 className="text-5xl font-black tracking-tighter leading-none mb-2">
            Product <span className="text-primary">Analytics</span>
          </h1>
          <p className="text-[12px] text-muted-foreground font-black uppercase tracking-[0.1em] mt-2 flex items-center gap-6 opacity-70">
            <span className="flex items-center gap-2 uppercase tabular-nums font-black"><Package className="size-3.5 text-primary" /> {fmtNum(insights.produtos.length)} UNIQUE SKUS PROCESSED</span>
          </p>
        </div>
      </header>

      {/* Main Stats Bento Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatBento label="Gross Revenue" value={fmtBRL(insights.vTotalProd)} sub="Faturamento Total" icon={DollarSign} color="emerald" delay={100} />
        <StatBento label="Total Taxes" value={fmtBRL(insights.vTotalTrib)} sub="Montante Tributário" icon={Layers} color="amber" delay={200} />
        <StatBento label="Average Burden" value={`${insights.cargaMedia.toFixed(2)}%`} sub="Carga Tributária Média" icon={TrendingUp} color="red" delay={300} />
        <StatBento label="Class A Items" value={fmtNum(insights.produtos.filter(p => p.cluster === 'A').length)} sub="80% do Faturamento" icon={PieIcon} color="primary" delay={400} />
      </div>

      {/* Analytics Bento Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Gráfico Curva ABC - Column 4 */}
        <div className="lg:col-span-4 glass-card p-8 flex flex-col border-primary/5 bg-white/[0.01]">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h3 className="text-sm font-black uppercase tracking-widest flex items-center gap-2">
                <PieIcon className="size-4 text-primary" /> Pareto Matrix (ABC)
              </h3>
              <p className="text-[9px] font-black text-muted-foreground uppercase tracking-widest mt-1">Clusterização de Faturamento</p>
            </div>
          </div>
          <div className="flex-1 min-h-[250px] relative flex items-center">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={insights.abcData} innerRadius={75} outerRadius={100} paddingAngle={10} dataKey="value" stroke="none">
                  {insights.abcData.map((entry: any, index: number) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(v: number) => fmtBRL(v)} contentStyle={{ backgroundColor: 'rgba(0,0,0,0.95)', border: 'none', borderRadius: '16px', color: 'white' }} />
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <span className="text-4xl font-black">{insights.produtos.filter(p => p.cluster === 'A').length}</span>
              <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Class A</span>
            </div>
          </div>
        </div>

        {/* NCM Analysis - Column 8 */}
        <div className="lg:col-span-8 glass-card p-8 flex flex-col border-primary/5">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h3 className="text-sm font-black uppercase tracking-widest flex items-center gap-2">
                <BarChart3 className="size-4 text-primary" /> NCM Concentration Matrix
              </h3>
              <p className="text-[9px] font-black text-muted-foreground uppercase tracking-widest mt-1">Análise de Carga por Código Fiscal</p>
            </div>
            <div className="px-3 py-1 glass-technical border-foreground/5 rounded-lg text-[9px] font-black uppercase text-muted-foreground">Top 10 NCMs</div>
          </div>
          <div className="h-[250px] flex-1">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={insights.ncmData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="currentColor" className="opacity-5" />
                <XAxis type="number" hide />
                <YAxis dataKey="ncm" type="category" axisLine={false} tickLine={false} tick={{ fontSize: 10, fontWeight: 900 }} width={80} />
                <Tooltip cursor={{ fill: 'rgba(var(--primary), 0.05)' }} contentStyle={{ backgroundColor: 'rgba(0,0,0,0.9)', border: 'none', borderRadius: '16px', color: 'white' }} />
                <Bar dataKey="vTotal" name="Faturamento" fill="#10B981" radius={[0, 4, 4, 0]} barSize={20} />
                <Bar dataKey="carga" name="Carga %" fill="#FF7A00" radius={[0, 4, 4, 0]} barSize={10} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Momentum de SKUs & Market Velocity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="glass-card p-8 border-emerald-500/5 bg-emerald-500/[0.02]">
           <h3 className="text-sm font-black uppercase tracking-widest flex items-center gap-2 text-emerald-500 mb-6">
             <TrendingUp className="size-4" /> Top Crescimento (Market Velocity)
           </h3>
           <div className="space-y-4">
              {insights.produtos.slice(0, 4).map((p, i) => (
                <div key={i} className="p-4 rounded-2xl glass-technical border-emerald-500/10 flex items-center justify-between">
                   <div className="flex flex-col gap-0.5">
                      <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">High Momentum SKU</span>
                      <span className="text-sm font-black truncate max-w-[250px]">{p.xProd}</span>
                   </div>
                   <div className="flex flex-col items-end">
                      <span className="text-[9px] font-black text-emerald-500 bg-emerald-500/10 px-2 py-0.5 rounded-full">HIGH MOMENTUM</span>
                      <span className="text-xs font-black tabular-nums">{fmtBRL(p.vTotal)}</span>
                   </div>
                </div>
              ))}
           </div>
        </div>

        <div className="glass-card p-8 border-red-500/5 bg-red-500/[0.02]">
           <h3 className="text-sm font-black uppercase tracking-widest flex items-center gap-2 text-red-500 mb-6">
             <TrendingDown className="size-4" /> Performance em Queda (Low Return)
           </h3>
           <div className="space-y-4">
              {insights.produtos.slice(-4).reverse().map((p, i) => (
                <div key={i} className="p-4 rounded-2xl glass-technical border-red-500/10 flex items-center justify-between">
                   <div className="flex flex-col gap-0.5">
                      <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Turnover Risk</span>
                      <span className="text-sm font-black truncate max-w-[250px]">{p.xProd}</span>
                   </div>
                   <div className="flex flex-col items-end">
                      <span className="text-[9px] font-black text-red-500 bg-red-500/10 px-2 py-0.5 rounded-full">LOW TURNOVER</span>
                      <span className="text-xs font-black tabular-nums">{fmtBRL(p.vTotal)}</span>
                   </div>
                </div>
              ))}
           </div>
        </div>
      </div>

      {/* Top 30 e Tabela */}
      <div className="grid lg:grid-cols-12 gap-4 items-start">
        {/* Ranking Top 30 */}
        <div className="lg:col-span-4 glass-card p-6 flex flex-col hover-glow animate-in-card" style={{ animationDelay: '700ms' }}>
          <div className="flex items-center justify-between mb-4 shrink-0">
            <h3 className="text-sm font-black uppercase tracking-widest flex items-center gap-2">
              <TrendingUp className="size-4 text-primary" /> Top 30 SKUs
            </h3>
          </div>
          <p className="text-[10px] text-muted-foreground mb-6 font-black uppercase tracking-widest">Tax Burden Analysis</p>
          <div className="flex flex-col gap-3 overflow-y-auto max-h-[500px] pr-2 custom-scrollbar">
            {top30.map((p, i) => (
              <div key={i} className="flex flex-col gap-1 group border-b border-foreground/5 pb-3 last:border-0 justify-center">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="size-6 rounded-md bg-muted/10 flex items-center justify-center text-[10px] font-black text-muted-foreground shrink-0">{i + 1}</div>
                    <span className="text-xs font-bold truncate flex-1" title={p.name}>{p.name}</span>
                  </div>
                  <div className="text-xs font-black text-emerald-500 tabular-nums">{fmtBRL(p.valor)}</div>
                </div>
                <div className="flex items-center justify-end">
                  <span className={cn("text-[10px] font-black", p.carga > 20 ? 'text-red-500' : 'text-amber-500')}>
                    {p.carga.toFixed(1)}% <span className="text-[8px] opacity-60 uppercase font-extrabold tracking-widest">Burden</span>
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Full Dataset Table */}
        <div className="lg:col-span-8 space-y-4 animate-in-card" style={{ animationDelay: '800ms' }}>
          {/* Search Panel */}
          <div className="glass-card p-4 flex items-center gap-4">
            <div className="relative flex-1 group">
              <Search className="size-4 absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground group-focus-within:text-primary transition-colors" />
              <input 
                value={q} 
                onChange={(e) => { setQ(e.target.value); setPage(0); }}
                placeholder="Search by Product Name, NCM or CFOP..."
                className="input-premium w-full pl-12 h-12 bg-transparent border-none focus:ring-0 text-sm"
              />
            </div>
            {q && <button onClick={() => setQ("")} className="text-[10px] font-black uppercase tracking-widest text-primary px-4">Clear</button>}
          </div>

          <div className="glass-card overflow-hidden">
            <div className="overflow-x-auto custom-scrollbar">
              <table className="w-full text-left">
                <thead className="bg-muted/10 text-muted-foreground">
                  <tr>
                    <th className="p-4 text-[10px] font-black uppercase tracking-[0.2em] border-r border-foreground/5">Cluster</th>
                    <th className="p-4 text-[10px] font-black uppercase tracking-[0.2em] border-r border-foreground/5">Product Name</th>
                    <th className="p-4 text-[10px] font-black uppercase tracking-[0.2em] cursor-pointer hover:text-primary transition-colors border-r border-foreground/5" onClick={() => toggleSort("ncm")}>
                      <div className="flex items-center gap-2">NCM <ArrowDownUp className="size-3" /></div>
                    </th>
                    <th className="p-4 text-[10px] font-black uppercase tracking-[0.2em] text-right cursor-pointer hover:text-primary transition-colors border-r border-foreground/5" onClick={() => toggleSort("qtd")}>
                      <div className="flex items-center justify-end gap-2">Qty <ArrowDownUp className="size-3" /></div>
                    </th>
                    <th className="p-4 text-[10px] font-black uppercase tracking-[0.2em] text-right cursor-pointer hover:text-primary text-amber-500 transition-colors border-r border-foreground/5" onClick={() => toggleSort("cargaTributaria")}>
                      <div className="flex items-center justify-end gap-2">Burden % <ArrowDownUp className="size-3" /></div>
                    </th>
                    <th className="p-4 text-[10px] font-black uppercase tracking-[0.2em] text-right cursor-pointer hover:text-primary text-emerald-500 transition-colors" onClick={() => toggleSort("vTotal")}>
                      <div className="flex items-center justify-end gap-2">Revenue <ArrowDownUp className="size-3" /></div>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-foreground/5">
                  {slice.map((p, i) => (
                    <tr key={i} className="hover:bg-primary/[0.03] transition-colors group">
                      <td className="p-4 border-r border-foreground/5">
                        <span className={cn(
                          "px-3 py-1 rounded-lg text-[10px] font-black border",
                          p.cluster === 'A' ? 'bg-primary/10 text-primary border-primary/20' :
                          p.cluster === 'B' ? 'bg-secondary/10 text-secondary border-secondary/20' :
                          'bg-muted/10 text-muted-foreground border-foreground/5'
                        )}>
                          CLASS {p.cluster}
                        </span>
                      </td>
                      <td className="p-4 border-r border-foreground/5">
                        <div className="font-bold text-sm max-w-[250px] truncate" title={p.xProd}>{p.xProd}</div>
                        <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mt-1">
                          CFOP: {p.cfop}
                        </div>
                      </td>
                      <td className="p-4 text-left border-r border-foreground/5 tabular-nums font-mono text-xs font-bold text-primary">{p.ncm}</td>
                      <td className="p-4 text-right border-r border-foreground/5 tabular-nums font-bold">{fmtNum(p.qtd)}</td>
                      <td className="p-4 text-right border-r border-foreground/5 font-black text-amber-500 tabular-nums">{p.cargaTributaria.toFixed(1)}%</td>
                      <td className="p-4 text-right font-black text-emerald-500 tabular-nums">{fmtBRL(p.vTotal)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Pagination */}
            <div className="p-4 border-t border-foreground/5 flex flex-col sm:flex-row items-center justify-between bg-muted/5 gap-4">
              <div className="flex items-center gap-4">
                <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Rows per page:</span>
                <select 
                  value={itemsPerPage} 
                  onChange={(e) => { setItemsPerPage(Number(e.target.value)); setPage(0); }}
                  className="bg-transparent border border-foreground/10 rounded-lg px-2 py-1 text-xs font-bold outline-none focus:ring-1 focus:ring-primary glass"
                >
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground ml-4">
                  Showing {slice.length} of {filtrados.length}
                </span>
              </div>
              
              <div className="flex items-center gap-4">
                <span className="text-[10px] font-black text-primary uppercase tracking-widest">Page {page + 1} / {totalPages}</span>
                <div className="flex gap-2">
                  <button disabled={page === 0} onClick={() => setPage(p => p - 1)} className="btn-glass h-10 px-4 disabled:opacity-20">
                    <ArrowLeft className="size-4" />
                  </button>
                  <button disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)} className="btn-glass h-10 px-4 disabled:opacity-20">
                    <ArrowRight className="size-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatBento({ label, value, sub, icon: Icon, color, delay }: any) {
  return (
    <div className={cn("glass-card p-5 group hover:glow-primary animate-in-card")} style={{ animationDelay: `${delay}ms` }}>
      <div className="flex items-center gap-3 mb-3">
        <div className={cn("size-10 rounded-xl flex items-center justify-center transition-colors duration-500", 
          color === 'primary' ? "bg-primary text-white" : 
          color === 'emerald' ? "bg-emerald-500 text-white" :
          color === 'amber' ? "bg-amber-500 text-white" :
          color === 'red' ? "bg-red-500 text-white" :
          "bg-muted/10 text-muted-foreground group-hover:bg-primary group-hover:text-white"
        )}>
          <Icon className="size-5" />
        </div>
        <span className="text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground">{label}</span>
      </div>
      <div className="text-3xl font-black tabular-nums tracking-tighter">{value}</div>
      <p className="text-[11px] font-bold text-muted-foreground mt-2">{sub}</p>
    </div>
  );
}
